import { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Check, BellRing, Send } from "lucide-react";
import CustomerSearch from "@/components/CustomerSearch";
import Pagination from "@/components/Pagination";
import { toast } from "sonner";
import ConfirmDelete from "@/components/ConfirmDelete";
import { deleteWithUndo } from "@/lib/deleteWithUndo";

const empty = { customer_id: "", title: "", notes: "", due_date: new Date().toISOString().slice(0, 10), priority: "medium", status: "pending" };

const prioColor = (p) => ({ low: "bg-[#121815] text-[#F8F9FA]", medium: "bg-blue-100 text-blue-300", high: "bg-red-500/15 text-red-300" }[p] || "");

export default function FollowUps() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [pageInfo, setPageInfo] = useState({ page: 1, total: 0, total_pages: 0, page_size: 50 });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      const { data } = await api.get(`/followups?${params.toString()}`);
      const items = Array.isArray(data) ? data : (data?.items || []);
      setRows(items);
      setPageInfo({
        page: data?.page ?? 1,
        page_size: data?.page_size ?? pageSize,
        total: data?.total ?? items.length,
        total_pages: data?.total_pages ?? 1,
      });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) return toast.error("Select customer");
    try {
      await api.post("/followups", form);
      toast.success("Follow-up added");
      setOpen(false); setForm(empty); load();
    } catch { toast.error("Failed"); }
  };

  const markDone = async (f) => {
    await api.patch(`/followups/${f.id}/status?status=${f.status === "done" ? "pending" : "done"}`);
    load();
  };

  const del = (f) =>
    deleteWithUndo({
      deletePath: `/followups/${f.id}`,
      restorePath: `/followups/${f.id}/restore`,
      label: f.title,
      entityLabel: "follow-up",
      onChange: load,
    });

  const sendReminder = async (f) => {
    try {
      const { data } = await api.post(`/followups/${f.id}/send-reminder`);
      if (data.status === "sent") toast.success(`SMS sent to ${data.to}`);
      else if (data.status === "logged") toast.info("Reminder logged (Twilio not configured)");
      else if (data.status === "failed") toast.error(`Failed: ${data.error || "unknown"}`);
    } catch { toast.error("Failed to send reminder"); }
  };

  const pending = rows.filter((r) => r.status === "pending");
  const done = rows.filter((r) => r.status === "done");

  return (
    <div className="p-8 lg:p-10 max-w-[1600px]">
      <PageHeader
        title="Follow-ups & Reminders"
        subtitle="Track calls, visits and reminders for customers."
        exportEntity="followups"
        testid="page-followups-header"
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(empty); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-followup-btn" className="bg-[#39FF14] hover:bg-[#2ECC71]">
                <Plus size={16} className="mr-2" /> New Follow-up
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">Schedule Follow-up</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Customer*</Label>
                  <CustomerSearch
                    testid="followup-customer-search"
                    value={form.customer_id}
                    onChange={(c) => setForm({ ...form, customer_id: c?.id || "" })}
                  />
                </div>
                <div><Label>Title*</Label><Input required data-testid="followup-title-input" placeholder="Call about payment, service visit…" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Due Date*</Label><Input required type="date" data-testid="followup-date-input" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                  <div>
                    <Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <DialogFooter>
                  <Button type="submit" data-testid="followup-submit-btn" className="bg-[#39FF14] hover:bg-[#2ECC71]">Save</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h3 className="font-heading text-lg font-bold text-[#F8F9FA] mb-3 flex items-center gap-2">
            <BellRing size={18} className="text-yellow-400" /> Pending ({pending.length})
          </h3>
          <div className="space-y-3" data-testid="followups-pending-list">
            {pending.length === 0 && <div className="text-[#8A8F98] text-sm bg-[#121815] border border-[#3A4640] rounded-md p-6 text-center">Nothing pending.</div>}
            {pending.map((f) => (
              <div key={f.id} className="bg-[#121815] border border-[#3A4640] rounded-md p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${prioColor(f.priority)}`}>{f.priority}</span>
                      <span className="text-xs text-[#8A8F98]">Due {formatDate(f.due_date)}</span>
                    </div>
                    <div className="font-medium text-[#F8F9FA]">{f.title}</div>
                    <div className="text-xs text-[#8A8F98] mt-0.5">{f.customer?.name} · {f.customer?.phone}</div>
                    {f.notes && <div className="text-sm text-[#8A8F98] mt-2">{f.notes}</div>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => sendReminder(f)} className="p-1.5 hover:bg-yellow-500/10 text-yellow-400 rounded border border-[#3A4640]" title="Send SMS reminder" data-testid={`send-reminder-${f.id}`}><Send size={14} /></button>
                    <button onClick={() => markDone(f)} className="p-1.5 hover:bg-green-50 text-[#39FF14] rounded border border-[#3A4640]" data-testid={`complete-followup-${f.id}`}><Check size={14} /></button>
                    <ConfirmDelete
                      itemLabel={f.title}
                      entityLabel="follow-up"
                      onConfirm={() => del(f)}
                      testid={`followup-${f.id}`}
                    >
                      <button className="p-1.5 hover:bg-red-500/10 text-red-600 rounded border border-[#3A4640]" data-testid={`delete-followup-${f.id}`}><Trash2 size={14} /></button>
                    </ConfirmDelete>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-heading text-lg font-bold text-[#F8F9FA] mb-3">Completed ({done.length})</h3>
          <div className="space-y-3">
            {done.length === 0 && <div className="text-[#8A8F98] text-sm bg-[#121815] border border-[#3A4640] rounded-md p-6 text-center">Nothing completed.</div>}
            {done.map((f) => (
              <div key={f.id} className="bg-[#0B0F0C] border border-[#3A4640] rounded-md p-4 opacity-70">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs text-[#8A8F98] mb-1">Done · {formatDate(f.due_date)}</div>
                    <div className="text-[#F8F9FA] line-through">{f.title}</div>
                    <div className="text-xs text-[#8A8F98] mt-0.5">{f.customer?.name}</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => markDone(f)} className="p-1.5 hover:bg-[#121815] rounded text-xs">Reopen</button>
                    <ConfirmDelete
                      itemLabel={f.title}
                      entityLabel="follow-up"
                      onConfirm={() => del(f)}
                      testid={`followup-done-${f.id}`}
                    >
                      <button className="p-1.5 hover:bg-red-500/10 text-red-600 rounded" data-testid={`delete-followup-done-${f.id}`}><Trash2 size={14} /></button>
                    </ConfirmDelete>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 bg-[#121815] border border-[#3A4640] rounded-md overflow-hidden">
        <Pagination
          page={pageInfo.page}
          totalPages={pageInfo.total_pages}
          total={pageInfo.total}
          pageSize={pageInfo.page_size}
          onPageChange={setPage}
          loading={loading}
          testid="followups-pagination"
        />
      </div>
    </div>
  );
}
